import { getSupabase } from '@infra/supabase/client';
import type { IPlansRepository, Plan, PlanCustomerType, PlanKind, UpsertPlanInput } from '@modules/plans';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

// Uma lista só: as quatro queries abaixo repetiam a mesma string, e esquecer uma
// delas ao adicionar coluna já custou tempo antes.
const PLAN_COLUMNS = 'id, workspace_id, name, description, unit_value, ativo, order_index, kind, customer_type';

type PlanRow = {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  unit_value: number;
  ativo: boolean;
  order_index: number;
  kind: string | null;
  customer_type: string | null;
};

const mapPlan = (row: PlanRow): Plan => ({
  ...row,
  unit_value: Number(row.unit_value || 0),
  order_index: Number(row.order_index || 0),
  kind: (row.kind === 'addon' ? 'addon' : 'plan') as PlanKind,
  customer_type: (row.customer_type === 'varejo' || row.customer_type === 'ambos'
    ? row.customer_type
    : 'corporate') as PlanCustomerType,
});

export class SupabasePlansRepository implements IPlansRepository {
  listPlans = async (workspaceId: string): Promise<Plan[]> => {
    const { data, error } = await client()
      .from('plans')
      .select(PLAN_COLUMNS)
      .eq('workspace_id', workspaceId)
      .eq('ativo', true)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []).map(row => mapPlan(row as PlanRow));
  };

  listAllPlans = async (workspaceId: string): Promise<Plan[]> => {
    const { data, error } = await client()
      .from('plans')
      .select(PLAN_COLUMNS)
      .eq('workspace_id', workspaceId)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []).map(row => mapPlan(row as PlanRow));
  };

  savePlan = async (input: UpsertPlanInput): Promise<Plan> => {
    const payload = {
      workspace_id: input.workspaceId,
      name: input.name,
      description: input.description || null,
      unit_value: input.unitValue,
      ativo: input.ativo ?? true,
      order_index: input.orderIndex ?? 0,
      kind: input.kind ?? 'plan',
      customer_type: input.customerType ?? 'corporate',
    };

    const query = input.id
      ? client().from('plans').update(payload).eq('id', input.id).select(PLAN_COLUMNS).single()
      : client().from('plans').insert(payload).select(PLAN_COLUMNS).single();

    const { data, error } = await query;
    if (error) throw error;
    return mapPlan(data as PlanRow);
  };

  setPlanActive = async (planId: string, ativo: boolean): Promise<void> => {
    const { error } = await client()
      .from('plans')
      .update({ ativo })
      .eq('id', planId);
    if (error) throw error;
  };
}
